import React, { useState, useMemo } from 'react';
import { Box, Card, CardContent, Typography, Chip, Button, keyframes } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { StationDivergenceRow, DivergenceRisk } from './types';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0px rgba(120,105,223,0); }
  50% { box-shadow: 0 0 18px rgba(120,105,223,0.18), 0 0 32px rgba(102,126,234,0.10); }
`;

const EASE_OUT: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

const RISK_STYLES: Record<DivergenceRisk, { label: string; color: string; bg: string; icon: string }> = {
  silent:  { label: 'risco silencioso', color: '#dc2626', bg: 'rgba(239,68,68,0.08)',  icon: '⚠️' },
  harsh:   { label: 'clientes duros',   color: '#0369a1', bg: 'rgba(14,165,233,0.08)', icon: 'ℹ️' },
  aligned: { label: 'alinhado',         color: '#15803d', bg: 'rgba(34,197,94,0.08)',  icon: '🟢' },
};

interface Props {
  rows: StationDivergenceRow[];
  loading: boolean;
}

const SkeletonCard: React.FC = () => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s infinite`,
      height: 320,
    }}
  />
);

const StationDivergenceTable: React.FC<Props> = ({ rows, loading }) => {
  const [visibleCount, setVisibleCount] = useState(5);

  // Só mostra linhas com ambos os sinais disponíveis pra comparação fazer sentido
  const filtered = useMemo(
    () => rows.filter((r) => r.avgStationRating != null && r.avgSentiment != null && r.delta != null),
    [rows],
  );

  if (loading) return <SkeletonCard />;

  if (!filtered.length) {
    return (
      <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center', border: '1px solid rgba(120,105,223,0.10)' }}>
        <LocationIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Nenhum posto com dados suficientes para comparar
        </Typography>
      </Card>
    );
  }

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: EASE_OUT }}
    >
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: '1px solid rgba(120,105,223,0.10)',
          animation: `${pulseGlow} 4s ease-in-out infinite`,
        }}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box
              sx={{
                width: 32, height: 32, borderRadius: 2,
                background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 3px 10px rgba(249,115,22,0.3)',
                flexShrink: 0,
              }}
            >
              <LocationIcon sx={{ color: '#fff', fontSize: 16 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
              Divergência por Posto
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1.75, ml: 5.75 }}>
            Ordem: maior divergência entre nota e sentimento → risco silencioso no topo
          </Typography>

          {/* Header row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 70px 70px 70px 130px',
              gap: 1.5,
              px: 1,
              pb: 0.5,
              borderBottom: '1px solid #f1f5f9',
              mb: 1,
            }}
          >
            {['Posto', 'Cliente', 'IA', 'Δ', 'Status'].map((h) => (
              <Typography
                key={h}
                variant="caption"
                sx={{
                  color: '#94a3b8',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.68rem',
                  letterSpacing: 0.4,
                  textAlign: h === 'Posto' ? 'left' : h === 'Status' ? 'left' : 'center',
                }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {/* Rows */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {visible.map((row, idx) => {
              const style = RISK_STYLES[row.risk];
              const deltaDisplay = row.delta != null
                ? (row.delta > 0 ? '+' : '') + row.delta.toFixed(1)
                : '—';
              return (
                <motion.div
                  key={row.stationId}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.04, duration: 0.25, ease: EASE_OUT }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 70px 70px 70px 130px',
                      gap: 1.5,
                      alignItems: 'center',
                      px: 1,
                      py: 1,
                      borderRadius: 1.5,
                      '&:hover': { bgcolor: '#f8fafc' },
                      transition: 'background 0.15s',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }} noWrap>
                      {row.stationName ?? row.stationId.slice(0, 8) + '…'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7869DF', fontWeight: 700, textAlign: 'center', fontSize: '0.9rem' }}>
                      {row.avgStationRating != null ? row.avgStationRating.toFixed(1) : '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#0ea5e9', fontWeight: 700, textAlign: 'center', fontSize: '0.9rem' }}>
                      {row.avgSentiment != null ? row.avgSentiment.toFixed(1) : '—'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: style.color,
                        fontWeight: 800,
                        textAlign: 'center',
                        fontSize: '0.9rem',
                      }}
                    >
                      {deltaDisplay}
                    </Typography>
                    <Chip
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <Box component="span" sx={{ fontSize: '0.8rem' }}>{style.icon}</Box>
                          <Box component="span">{style.label}</Box>
                        </Box>
                      }
                      size="small"
                      sx={{
                        bgcolor: style.bg,
                        color: style.color,
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        height: 22,
                        border: `1px solid ${style.color}30`,
                        '& .MuiChip-label': { px: 0.75, display: 'flex', alignItems: 'center' },
                      }}
                    />
                  </Box>
                </motion.div>
              );
            })}
          </Box>

          {hasMore && (
            <Button
              fullWidth
              size="small"
              endIcon={<ExpandMoreIcon />}
              onClick={() => setVisibleCount((v) => v + 5)}
              sx={{
                mt: 1.5,
                color: '#79CF62',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': { bgcolor: 'rgba(121,207,98,0.08)' },
              }}
            >
              Ver mais ({filtered.length - visibleCount} restantes)
            </Button>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default StationDivergenceTable;
